import { useEffect, useState } from 'react';
import { FolderKanban } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import type { Task, TaskStatus, TaskPriority, UpdateTaskPayload, ProjectOption, UserOption } from '../types';

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Done' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const PRIORITY_TONE: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  low: 'neutral',
  medium: 'info',
  high: 'warning',
  critical: 'danger',
};

interface TaskDrawerProps {
  task: Task | null;
  projects: ProjectOption[];
  users: UserOption[];
  isAdmin: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave: (id: string, payload: UpdateTaskPayload) => void;
}

type DrawerForm = {
  title: string;
  description: string;
  requirements: string;
  project_id: string;
  assigned_to: string[];
  status: TaskStatus;
  priority: TaskPriority;
  due_date: string;
  due_time: string;
};

function toForm(task: Task): DrawerForm {
  return {
    title: task.title ?? '',
    description: task.description ?? '',
    requirements: task.requirements ?? '',
    project_id: task.project_id ?? '',
    assigned_to: task.assignees?.map((a) => a.id) ?? [],
    status: task.status ?? 'todo',
    priority: task.priority ?? 'medium',
    due_date: task.due_date ?? '',
    due_time: task.due_time ?? '',
  };
}

export function TaskDrawer({ task, projects, users, isAdmin, saving, onClose, onSave }: TaskDrawerProps) {
  const [form, setForm] = useState<DrawerForm | null>(null);

  useEffect(() => {
    if (task) setForm(toForm(task));
    else setForm(null);
  }, [task]);

  if (!task || !form) return null;

  const set = <K extends keyof DrawerForm>(key: K, value: DrawerForm[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    const payload: UpdateTaskPayload = {
      title: form.title.trim(),
      description: form.description.trim(),
      requirements: form.requirements.trim(),
      project_id: form.project_id || null,
      assigned_to: form.assigned_to,
      status: form.status,
      priority: form.priority,
      due_date: form.due_date || null,
      due_time: form.due_time || undefined,
    };
    onSave(task.id, payload);
  };

  const projectOptions = [
    { value: '', label: 'No Project' },
    ...projects.map((p) => ({ value: p.id, label: p.name })),
  ];

  return (
    <Drawer open={!!task} onClose={onClose} title={form.title || 'Task Details'} width="w-[520px]">
      <form onSubmit={handleSave} className="flex h-full flex-col gap-5">
        {/* Header meta */}
        <div className="flex flex-wrap gap-2">
          <Badge tone={PRIORITY_TONE[form.priority] ?? 'neutral'}>
            {form.priority.toUpperCase()}
          </Badge>
          {task.project_name && (
            <span className="inline-flex items-center gap-1 rounded-card-sm bg-surface-variant px-2 py-0.5 text-body-sm text-on-surface-variant">
              <FolderKanban className="h-3.5 w-3.5" /> {task.project_name}
            </span>
          )}
        </div>

        {/* Title */}
        <Input
          label="Title"
          name="drawer-task-title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          required
        />

        {/* Status + Priority */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Status"
            name="drawer-task-status"
            value={form.status}
            onChange={(e) => set('status', e.target.value as TaskStatus)}
            options={STATUS_OPTIONS}
          />
          <Select
            label="Priority"
            name="drawer-task-priority"
            value={form.priority}
            onChange={(e) => set('priority', e.target.value as TaskPriority)}
            options={PRIORITY_OPTIONS}
          />
        </div>

        {/* Project + Due Date */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Project"
            name="drawer-task-project"
            value={form.project_id}
            onChange={(e) => set('project_id', e.target.value)}
            options={projectOptions}
          />
          <Input
            label="Due Date"
            name="drawer-task-due"
            type="date"
            value={form.due_date}
            onChange={(e) => set('due_date', e.target.value)}
          />
        </div>

        {/* Assignees (admin only) */}
        {isAdmin && (
          <div>
            <label htmlFor="drawer-assignees" className="mb-1.5 block text-body-sm font-medium text-on-surface">
              Assign To
            </label>
            <select
              id="drawer-assignees"
              multiple
              value={form.assigned_to}
              onChange={(e) =>
                set(
                  'assigned_to',
                  Array.from(e.target.selectedOptions).map((o) => o.value).filter(Boolean),
                )
              }
              className="h-24 w-full rounded-DEFAULT border border-outline bg-surface px-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Description */}
        <div>
          <label htmlFor="drawer-task-desc" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Description
          </label>
          <textarea
            id="drawer-task-desc"
            rows={3}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {/* Requirements */}
        <div>
          <label htmlFor="drawer-task-reqs" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Requirements
          </label>
          <textarea
            id="drawer-task-reqs"
            rows={2}
            value={form.requirements}
            onChange={(e) => set('requirements', e.target.value)}
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {/* Footer */}
        <div className="mt-auto flex justify-end gap-3 border-t border-outline pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            Save Changes
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
